"use client";

import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

interface AnchorMenuItem {
  title: string;
  anchor: string;
}

interface HeaderProps {
  variant?: "default" | "meetup" | "estagio" | "devs";
}

const meetupMenuItems: AnchorMenuItem[] = [
  { title: "O evento", anchor: "#evento" },
  { title: "Palestrantes", anchor: "#palestrantes" },
  { title: "O que esperar", anchor: "#o-que-esperar" },
  { title: "Por que participar", anchor: "#por-que-participar" },
  { title: "Localização", anchor: "#localizacao" },
];

const estagioMenuItems: AnchorMenuItem[] = [
  { title: "O Programa", anchor: "#o-programa" },
  { title: "Requisitos", anchor: "#requisitos" },
  { title: "Benefícios", anchor: "#beneficios" },
  { title: "Processo seletivo", anchor: "#processo-seletivo" },
];

const devsMenuItems: AnchorMenuItem[] = [
  { title: "O que é?", anchor: "#o-que-e" },
  { title: "Etapas", anchor: "#etapas" },
  { title: "Diferenciais", anchor: "#diferenciais" },
  { title: "Requisitos", anchor: "#requisitos" },
  { title: "Jeito Agi", anchor: "#jeito-agi" },
  { title: "Fala Dev!", anchor: "#fala-dev" },
];

const ctaConfig = {
  meetup: { text: "Inscreva-se", href: "https://pt.surveymonkey.com/r/MeetupAgibank", active: true },
  estagio: { text: "Fazer inscrição!", href: "https://grnh.se/a102yrkh8us", active: true },
  devs: { text: "Inscrições encerradas", href: "#", active: false },
};

function smoothScrollTo(anchor: string) {
  const element = document.querySelector(anchor);
  if (element) {
    const headerHeight = 72; // Altura aproximada do header
    const elementPosition = element.getBoundingClientRect().top + window.scrollY;
    const offsetPosition = elementPosition - headerHeight;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  }
}

export default function Header({ variant = "default" }: HeaderProps) {
  const [openMenu, setOpenMenu] = useState<"vagas" | "programas" | null>(null);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const vagasItems = [
    { title: "Comercial", href: "/vagas?area=comercial" },
    { title: "Corporativo", href: "/vagas?area=corporativo" },
    { title: "Tecnologia", href: "/vagas?area=tecnologia" },
  ];

  const programasItems = [
    { title: "Estágio", href: "/estagio" },
    { title: "Formação de Devs nativos em IA", href: "/formacao-devs-ia" },
  ];

  // Anchor menu for special pages
  if (variant !== "default") {
    const menuItems =
      variant === "meetup"
        ? meetupMenuItems
        : variant === "estagio"
        ? estagioMenuItems
        : devsMenuItems;
    const cta = ctaConfig[variant];

    return (
      <nav className="py-3 pb-4 relative bg-white z-[9999999]">
        <div className="w-[1270px] px-4 max-w-full m-auto flex items-center justify-between">
          <Link href="/">
            <Image
              src="/images/header-logo-positive.svg"
              alt="Agibank Logo"
              width={64}
              height={44}
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-6">
            {menuItems.map((item) => (
              <button
                key={item.anchor}
                type="button"
                onClick={() => smoothScrollTo(item.anchor)}
                className="text-blue-dark font-medium text-[15px] transition-colors hover:text-primary-main whitespace-nowrap"
              >
                {item.title}
              </button>
            ))}

            {cta.active ? 
              <a
                href={cta.href}
                target="_blank"
                className="px-6 py-2.5 rounded-full text-[15px] font-bold transition-colors whitespace-nowrap bg-primary-main text-white hover:bg-secondary-main hover:text-blue-dark"
              >
                {cta.text}
              </a>
            : 
              <button
                type="button"
                onClick={() => cta.active && smoothScrollTo(cta.href)}
                className={`px-6 py-2.5 rounded-full text-[15px] font-bold transition-colors whitespace-nowrap ${
                  cta.active
                    ? "bg-secondary-main text-blue-dark hover:bg-opacity-90"
                    : "bg-gray-300 text-gray-600 cursor-not-allowed"
                }`}
                disabled={!cta.active}
              >
                {cta.text}
              </button>
            }

          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center rounded-lg p-2 text-primary-main transition-opacity hover:opacity-80"
            aria-label="Abrir menu"
            onClick={() => setIsMobileOpen((open) => !open)}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path
                d="M3 6H21M3 12H21M3 18H21"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden absolute left-0 right-0 top-full z-50 bg-white shadow-lg transition-opacity duration-200 ease-out ${
            isMobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          }`}
        >
          <div className="container-default py-4">
            <nav className="flex flex-col gap-3">
              {menuItems.map((item) => (
                <button
                  key={item.anchor}
                  type="button"
                  onClick={() => {
                    smoothScrollTo(item.anchor);
                    setIsMobileOpen(false);
                  }}
                  className="text-blue-dark font-medium text-base text-left py-2 transition-colors hover:text-primary-main"
                >
                  {item.title}
                </button>
              ))}
              <a
                href={cta.href}
                target={cta.active ? "_blank" : undefined}
                className={`mt-2 px-6 py-3 rounded-full text-base font-bold transition-colors ${
                  cta.active
                    ? "bg-secondary-main text-blue-dark hover:bg-opacity-90"
                    : "bg-gray-300 text-gray-600 cursor-not-allowed"
                }`}
              >
                {cta.text}
              </a>
            </nav>
          </div>
        </div>
      </nav>
    );
  }

  // Default header with dropdowns
  return (
    <nav className="py-3 pb-4 relative bg-white z-[9999999]">
      <div className="w-[1270px] px-4 max-w-full m-auto flex items-center justify-between">
        <Link href="/">
          <Image
            src="/images/header-logo-positive.svg"
            alt="Agibank Logo"
            width={64}
            height={44}
          />
        </Link>
        <div className="hidden md:flex items-center gap-6">
          <button
            type="button"
            onClick={() => smoothScrollTo("#jeito-agi")}
            className="text-primary-main font-semibold text-base transition-opacity hover:opacity-80"
          >
            Jeito Agi
          </button>

          <div
            className="relative py-2"
             onMouseEnter={() => setOpenMenu("vagas")}
            onMouseLeave={() => setOpenMenu(null)}
          >
            <Link
              href="/vagas"
              className="flex items-center gap-1 text-primary-main font-semibold text-base transition-opacity hover:opacity-80"
              onClick={(event) => {
                event.preventDefault();
                setOpenMenu((current) => (current === "vagas" ? null : "vagas"));
              }}
            >
              <span>Vagas</span>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M7 9.5L12 14.5L17 9.5H7Z" fill="currentColor" />
              </svg>
            </Link>
            <div
              className={`absolute z-10 right-0 top-full w-64 rounded-2xl bg-white shadow-lg ring-1 ring-black/5 transition duration-200 ease-out ${
                openMenu === "vagas"
                  ? "opacity-100 pointer-events-auto"
                  : "opacity-0 pointer-events-none"
              }`}
            >
              <div className="py-3">
                {vagasItems.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className="block px-4 py-2 text-sm text-text-primary transition-colors hover:bg-background-default"
                    onClick={() => setOpenMenu(null)}
                  >
                    {item.title}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div
            className="relative py-2"
            onMouseEnter={() => setOpenMenu("programas")}
            onMouseLeave={() => setOpenMenu(null)}
          >
            <Link
              href="/#programas"
              className="flex items-center gap-1 text-primary-main font-semibold text-base transition-opacity hover:opacity-80"
              onClick={(event) => {
                event.preventDefault();
                setOpenMenu((current) => (current === "programas" ? null : "programas"));
              }}
            >
              <span>Programas</span>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M7 9.5L12 14.5L17 9.5H7Z" fill="currentColor" />
              </svg>
            </Link>
            <div
              className={`absolute z-10 right-0 top-full w-64 rounded-2xl bg-white shadow-lg ring-1 ring-black/5 transition duration-200 ease-out ${
                openMenu === "programas"
                  ? "opacity-100 pointer-events-auto"
                  : "opacity-0 pointer-events-none"
              }`}
            >
              <div className="py-3">
                {programasItems.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className="block px-4 py-2 text-sm text-text-primary transition-colors hover:bg-background-default"
                    onClick={() => setOpenMenu(null)}
                  >
                    {item.title}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          className="md:hidden inline-flex items-center justify-center rounded-lg p-2 text-primary-main transition-opacity hover:opacity-80"
          aria-label="Abrir menu"
          onClick={() => setIsMobileOpen((open) => !open)}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path
              d="M3 6H21M3 12H21M3 18H21"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <div
        className={`md:hidden absolute left-0 right-0 top-full z-50 bg-white transition-opacity duration-200 ease-out ${
          isMobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        style={{ height: "calc(100vh - 72px)" }}
      >
        <div className="container-default py-6 h-full overflow-y-auto">
          <nav className="flex flex-col gap-6 text-primary-main">
            <button
              type="button"
              onClick={() => {
                smoothScrollTo("#jeito-agi");
                setIsMobileOpen(false);
              }}
              className="text-base font-semibold text-left cursor-pointer"
            >
              Jeito Agi
            </button>

            <div className="flex flex-col gap-2">
              <span className="text-base font-semibold">Vagas</span>
              <div className="flex flex-col gap-2 pl-3 text-sm opacity-90">
                {vagasItems.map((item) => (
                  <Link key={item.href} href={item.href} onClick={() => setIsMobileOpen(false)}>
                    {item.title}
                  </Link>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-base font-semibold">Programas</span>
              <div className="flex flex-col gap-2 pl-3 text-sm opacity-90">
                {programasItems.map((item) => (
                  <Link key={item.href} href={item.href} onClick={() => setIsMobileOpen(false)}>
                    {item.title}
                  </Link>
                ))}
              </div>
            </div>
          </nav>
        </div>
      </div>
    </nav>
  );
}
